import type {
  QueryClient,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import {
  EventIdPublic,
  type UserIdPublic,
} from "@shared/schemas/branded-id.schemas";
import {
  EventHostSchema,
  RemoveEventHostResultSchema,
  type EventHost,
  type RemoveEventHostResult,
} from "@shared/schemas/event-host.schemas";
import type { EmailAddress } from "@shared/schemas/email.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { createEventHostsApi } from "./event-hosts.api";

export const EVENT_HOSTS_QUERY_KEYS = {
  byEvent: (eventIdPublic: EventIdPublic) =>
    ["event-hosts", eventIdPublic] as const,
} as const;

const parseHosts = (dtos: readonly EventHost[]): readonly EventHost[] =>
  dtos.map((dto) => EventHostSchema.parse(dto));

interface GetEventHostsOptions {
  readonly enabled?: boolean;
}

interface AddEventHostVars {
  readonly eventIdPublic: EventIdPublic;
  readonly email: EmailAddress;
}

interface RemoveEventHostVars {
  readonly eventIdPublic: EventIdPublic;
  readonly userIdPublic: UserIdPublic;
}

export class EventHostHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createEventHostsApi(apiClient);
  }

  createHooks() {
    return {
      useGetEventHosts: (
        eventIdPublic: EventIdPublic,
        options: GetEventHostsOptions = {},
      ): UseQueryResult<readonly EventHost[]> => {
        const { enabled = true } = options;

        return this.query({
          key: EVENT_HOSTS_QUERY_KEYS.byEvent(eventIdPublic),
          fn: () => this.api.getByEvent(eventIdPublic),
          parse: parseHosts,
          enabled: enabled && EventIdPublic.unwrap(eventIdPublic).length > 0,
        });
      },

      useAddEventHost: (): UseMutationResult<
        EventHost,
        Error,
        AddEventHostVars
      > =>
        this.mutation({
          fn: (vars: AddEventHostVars) =>
            this.api.add(vars.eventIdPublic, { email: vars.email }),
          parse: (dto) => EventHostSchema.parse(dto),
          onSuccess: (_host, vars): void => {
            this.invalidateQueries(
              EVENT_HOSTS_QUERY_KEYS.byEvent(vars.eventIdPublic),
            );
          },
        }),

      useRemoveEventHost: (): UseMutationResult<
        RemoveEventHostResult,
        Error,
        RemoveEventHostVars
      > =>
        this.mutation({
          fn: (vars: RemoveEventHostVars) =>
            this.api.remove(vars.eventIdPublic, vars.userIdPublic),
          parse: (dto) => RemoveEventHostResultSchema.parse(dto),
          onSuccess: (_result, vars): void => {
            this.invalidateQueries(
              EVENT_HOSTS_QUERY_KEYS.byEvent(vars.eventIdPublic),
            );
          },
        }),
    };
  }
}
